import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { Users, Settings, TrendingUp, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralManagementPage = () => {
  const { token } = useAdmin();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [configRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/referrals/config`, { headers }),
          fetch(`${API_URL}/api/admin/referrals/stats`, { headers })
        ]);
        const configData = await configRes.json();
        const statsData = await statsRes.json();
        if (configData.success) setConfig(configData.config);
        if (statsData.success) setStats(statsData.stats);
      } catch (e) {
        console.error('Failed to fetch referral data:', e);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/referrals/config`, {
        method: 'PUT', headers,
        body: JSON.stringify(config)
      });
      const data = await res.json();
      if (data.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch {
      alert('Failed to save');
    }
    setSaving(false);
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-referral-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Referral Program</h1>
        <p className="text-gray-600 mt-1">Configure referral rewards for riders and drivers</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-600" />
              <span className="text-xs text-gray-500 uppercase font-medium">Total Referrals</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_referrals}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-gray-500 uppercase font-medium">Active</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats.active_referrals}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="w-4 h-4 text-amber-600" />
              <span className="text-xs text-gray-500 uppercase font-medium">Completed</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.completed_referrals}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Settings className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-gray-500 uppercase font-medium">Credits Used</span>
            </div>
            <p className="text-2xl font-bold text-purple-600">{stats.credits_used}</p>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Referral Settings</h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-sm text-gray-600">{config.enabled ? 'Enabled' : 'Disabled'}</span>
            <button
              onClick={() => setConfig({ ...config, enabled: !config.enabled })}
              data-testid="referral-enable-toggle"
              className={`w-11 h-6 rounded-full transition-colors ${config.enabled ? 'bg-green-600' : 'bg-gray-300'}`}
            >
              <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow ${config.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Rider Reward */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-3">Rider Referrer Reward</h4>
            <label className="block text-sm text-gray-700 mb-1">Discount % per successful referral</label>
            <input
              type="number"
              min="1"
              max="100"
              value={config.rider_discount_percent}
              onChange={(e) => setConfig({ ...config, rider_discount_percent: Number(e.target.value) })}
              data-testid="rider-discount-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Riders earn this % off their next ride when their referral completes the required trips</p>
          </div>

          {/* Driver Reward */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <h4 className="font-medium text-amber-900 mb-3">Driver Referrer Reward</h4>
            <label className="block text-sm text-gray-700 mb-1">Bonus amount (MWK) per successful referral</label>
            <input
              type="number"
              min="0"
              value={config.driver_bonus_amount}
              onChange={(e) => setConfig({ ...config, driver_bonus_amount: Number(e.target.value) })}
              data-testid="driver-bonus-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">Drivers earn this amount added directly to their balance</p>
          </div>

          {/* Referee Reward */}
          <div className="p-4 bg-green-50 rounded-xl border border-green-200">
            <h4 className="font-medium text-green-900 mb-3">New User (Referee) Reward</h4>
            <label className="block text-sm text-gray-700 mb-1">Welcome discount % for new user</label>
            <input
              type="number"
              min="1"
              max="100"
              value={config.referee_discount_percent}
              onChange={(e) => setConfig({ ...config, referee_discount_percent: Number(e.target.value) })}
              data-testid="referee-discount-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">New users joining via referral get this % off their rides</p>
          </div>

          {/* Trip Requirements & Validity */}
          <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
            <h4 className="font-medium text-purple-900 mb-3">Requirements & Limits</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Required trips for referrer reward</label>
                <select
                  value={config.required_trips}
                  onChange={(e) => setConfig({ ...config, required_trips: Number(e.target.value) })}
                  data-testid="required-trips-select"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value={1}>1 trip</option>
                  <option value={2}>2 trips</option>
                  <option value={3}>3 trips</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Credit validity (days)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={config.validity_days}
                  onChange={(e) => setConfig({ ...config, validity_days: Number(e.target.value) })}
                  data-testid="validity-days-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Save */}
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-referral-config-btn"
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          {saved && <span className="text-sm text-green-600 font-medium">Saved successfully!</span>}
        </div>
      </div>
    </div>
  );
};

export default ReferralManagementPage;
